import React, { useState, useEffect, useCallback } from 'react';
import { twoFactorSetupStart, twoFactorSetupConfirm } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import './TwoFactor.css';

const copyText = async (text, okMsg) => {
  try {
    await navigator.clipboard.writeText(text);
    showSuccess(okMsg);
  } catch (e) {
    showError('Could not copy — select the text and copy it by hand');
  }
};

// Authenticator-app enrolment, shared by the forced setup page and My account.
export default function TwoFactorEnrol({ onDone, onCancel, cancelLabel = 'Cancel' }) {
  const [setup, setSetup] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const start = useCallback(async () => {
    setLoadError('');
    setSetup(null);
    try {
      setSetup(await twoFactorSetupStart());
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Could not start the setup. Please try again.');
    }
  }, []);

  useEffect(() => { start(); }, [start]);

  const confirm = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\s+/g, '');
    if (!clean) return;
    setBusy(true);
    setError('');
    try {
      await twoFactorSetupConfirm(clean);
      if (onDone) onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'That code was not accepted. Check the code and try again.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="tf-enrol">
        <p className="tf-error" role="alert">{loadError}</p>
        <div className="tf-actions">
          <button type="button" className="btn btn-primary btn-small" onClick={start}>Try again</button>
          {onCancel && <button type="button" className="btn btn-outline btn-small" onClick={onCancel}>{cancelLabel}</button>}
        </div>
      </div>
    );
  }
  if (!setup) return <div className="tf-enrol tf-muted">Preparing your setup…</div>;

  const grouped = String(setup.secret || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
  return (
    <form className="tf-enrol" onSubmit={confirm}>
      <ol className="tf-steps">
        <li>Install Google Authenticator, Microsoft Authenticator or Authy on your phone.</li>
        <li>Scan this code, then enter the 6-digit code it shows.</li>
      </ol>
      {setup.qr_data_url && <img className="tf-qr" src={setup.qr_data_url} alt="Authenticator QR code" />}
      <small className="tf-hint">Can&apos;t scan? Enter this key in the app instead:</small>
      <div className="tf-key-row">
        <span className="tf-key">{grouped}</span>
        <button type="button" className="btn btn-outline btn-small" onClick={() => copyText(String(setup.secret || '').replace(/\s+/g, ''), 'Key copied')}>Copy</button>
      </div>
      <input
        className="form-control tf-code-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={7}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="6-digit code"
        aria-label="6-digit code from your authenticator app"
      />
      {error && <p className="tf-error" role="alert">{error}</p>}
      <div className="tf-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Turn on'}</button>
        {onCancel && <button type="button" className="btn btn-outline" onClick={onCancel} disabled={busy}>{cancelLabel}</button>}
      </div>
    </form>
  );
}
